/**
 * StatusPill - Unified status display component
 * 
 * Part of the Unified Detail Page Design System v1.0
 * 
 * @param {string} status - Current status value
 * @param {object} config - Status configuration map { STATUS: { label, color, icon } }
 * @param {function} onClick - Click handler (if editable)
 * @param {boolean} editable - Whether status can be changed
 * @param {string} testId - Test ID for automation
 */

import React from 'react';

const StatusPill = ({ 
  status, 
  config = {}, 
  onClick, 
  editable = false, 
  testId = '',
  size = 'default' // 'small' | 'default' | 'large'
}) => {
  const statusConfig = config[status] || config.NONE || { 
    label: status || 'Unknown', 
    color: 'bg-gray-100 text-gray-700' 
  };
  const Icon = statusConfig.icon;
  
  const sizeClasses = {
    small: 'px-2 py-0.5 text-xs gap-1',
    default: 'px-3 py-1.5 text-sm gap-1.5',
    large: 'px-4 py-2 text-base gap-2'
  };
  
  const iconSizes = {
    small: 'w-3 h-3',
    default: 'w-3.5 h-3.5',
    large: 'w-4 h-4'
  };
  
  const baseClasses = `inline-flex items-center rounded-full font-medium transition-all ${sizeClasses[size]}`;
  const interactiveClasses = editable 
    ? 'cursor-pointer hover:opacity-80 hover:shadow-sm' 
    : 'cursor-default';
  
  const Component = editable && onClick ? 'button' : 'span';
  
  return (
    <Component
      onClick={editable ? onClick : undefined}
      disabled={!editable}
      data-testid={testId}
      className={`${baseClasses} ${statusConfig.color} ${interactiveClasses}`}
    >
      {Icon && <Icon className={iconSizes[size]} />}
      {statusConfig.label}
    </Component>
  );
};

// Common status configurations for reuse
export const STATUS_CONFIGS = {
  OPS: {
    OPEN: { label: 'Open', color: 'bg-blue-100 text-blue-700' },
    IN_PROGRESS: { label: 'In Progress', color: 'bg-purple-100 text-purple-700' },
    COMPLETED: { label: 'Completed', color: 'bg-green-100 text-green-700' },
  },
  INVOICE: {
    NONE: { label: 'None', color: 'bg-gray-100 text-gray-500' },
    ISSUED: { label: 'Issued', color: 'bg-blue-100 text-blue-700' },
    SENT: { label: 'Sent', color: 'bg-indigo-100 text-indigo-700' },
    PAID: { label: 'Paid', color: 'bg-green-100 text-green-700' },
    VOID: { label: 'Void', color: 'bg-red-100 text-red-700' },
  },
  FULFILLMENT: {
    NONE: { label: 'None', color: 'bg-gray-100 text-gray-500' },
    READY: { label: 'Ready', color: 'bg-cyan-100 text-cyan-700' },
    SHIPPED: { label: 'Shipped', color: 'bg-blue-100 text-blue-700' },
  },
  TASK: {
    open: { label: 'Open', color: 'bg-blue-100 text-blue-700' },
    in_progress: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-700' },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-700' },
    cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500' },
  },
  LEAD: {
    new: { label: 'New', color: 'bg-blue-100 text-blue-700' },
    contacted: { label: 'Contacted', color: 'bg-indigo-100 text-indigo-700' },
    qualified: { label: 'Qualified', color: 'bg-purple-100 text-purple-700' },
    proposal: { label: 'Proposal', color: 'bg-yellow-100 text-yellow-700' },
    negotiation: { label: 'Negotiation', color: 'bg-orange-100 text-orange-700' },
    won: { label: 'Won', color: 'bg-green-100 text-green-700' },
    lost: { label: 'Lost', color: 'bg-red-100 text-red-700' },
    dormant: { label: 'Dormant', color: 'bg-gray-100 text-gray-500' },
  },
};

export default StatusPill;
